/**
 * Shrink a captured screenshot before it goes to a vision model. Captures come
 * back at device pixels (2x on most laptops) as PNG, often several MB; a vision
 * model reads a 1600 px JPEG just as well for "where is the edit icon" and it
 * uploads in a fraction of the time. Returns the original data URL when it is
 * already small enough or when decoding fails (the caller still gets an image).
 */
const maxImageDataUrlChars = 1_500_000;

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () =>
            reject(new Error('The screenshot could not be decoded.'));
        image.src = dataUrl;
    });

export const downscaleImageDataUrl = async (
    dataUrl: string,
    maxDimension = 1600,
    quality = 0.85
): Promise<string> => {
    let image: HTMLImageElement;
    try {
        image = await loadImage(dataUrl);
    } catch {
        return dataUrl;
    }

    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (!width || !height) {
        return dataUrl;
    }

    const scale = Math.min(1, maxDimension / Math.max(width, height));
    if (scale === 1 && dataUrl.length <= maxImageDataUrlChars) {
        return dataUrl;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext('2d');
    if (!context) {
        return dataUrl;
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    try {
        return canvas.toDataURL('image/jpeg', quality);
    } catch {
        return dataUrl;
    }
};
